import React, { useContext, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment-timezone';

import { GET_LOAD_DETAIL_COMMITTED_CAPACITY, GET_LOCATIONS_SUGGESTIONS } from 'api/queries';
import { BOOK_LOAD_IN_COMMITTED_CAPACITY } from 'api/mutations';
import type { LoadType } from 'sharedTypes/loads/loadType';
import { BaseLocationType } from 'sharedTypes/location/locationTypes';

import { CommittedCapacityLandingPageSpinner } from '../LandingPage/LandingPage';
import { ErrorBooking, SuccessFullyBooking } from '../ProcessingTender/ProcessingTender';

import { UserContext } from 'components/Providers/Providers';

import { useBookedTender } from '../../hooks/useBookedTender';

type Props = {
    carrierId: number;
    changeTenderStatus: () => void;
    tenderDetails: any;
    tenderNumber: number;
}

export default function BookedTender({ carrierId, changeTenderStatus, tenderDetails, tenderNumber }: Props) {
  const currentUser = useContext(UserContext);
  const {
    loadingMessage,
    onCompletedLoadBooking,
    onErrorLoadBooking,
    setLoadingMessage,
    wasSuccessfullBook
  } = useBookedTender({ changeTenderStatus, tenderDetails });

  const {
    error: errorLoadDetails,
    data: loadDetailsData = { getLoadByIdForCommittedCapacity: {} as LoadType },
    loading: loadingLoadDetails
  } = useQuery(GET_LOAD_DETAIL_COMMITTED_CAPACITY, {
    variables: { loadNumber: tenderDetails?.loadNumber },
    skip: isNaN(tenderDetails.loadNumber ? tenderDetails.loadNumber : NaN) || !currentUser.UserId,
    fetchPolicy: 'no-cache'
  });

  const loadDetails = Object.keys(loadDetailsData.getLoadByIdForCommittedCapacity).length
    ? loadDetailsData.getLoadByIdForCommittedCapacity
    : null

  const { data: locationSuggestions = { getLocationSuggestions: { data: [] } } } = useQuery(GET_LOCATIONS_SUGGESTIONS, {
    variables: { searchInput: loadDetails ? `${loadDetails.PickupEarlyCity}, ${loadDetails.PickupEarlyStateCode}` : '' },
    skip: !loadDetails,
    fetchPolicy: 'no-cache'
  });

  const [
    bookItNow,
    {
      error: bookError,
      loading: bookLoading
    }
  ] = useMutation(BOOK_LOAD_IN_COMMITTED_CAPACITY, {
    onCompleted: onCompletedLoadBooking,
    onError: onErrorLoadBooking,
    context: {
      timeout: 100000
    }
  });

  const doNotExistData = !loadDetails &&
    !loadingLoadDetails &&
    (isNaN(tenderNumber) || isNaN(tenderDetails.loadNumber));

  useEffect(() => {
    const locationSuggestionsList = locationSuggestions.getLocationSuggestions.data;
    const selectedLocationSuggestion = loadDetails && locationSuggestionsList.length
      ? locationSuggestionsList.find((location: BaseLocationType) => location.CityName.toLowerCase() === loadDetails.PickupEarlyCity.toLowerCase())
      : null;

    if (tenderDetails && loadDetails && selectedLocationSuggestion) {
      const bookLoadVariables = {
        CarrierId: carrierId,
        IsCarrierAddFsc: tenderDetails.isCarrierAddFsc,
        LoadNumber: tenderDetails.loadNumber,
        TopSpend: loadDetails.TopSpend,
        RowVersion: loadDetails.RowVersion,
        LocationIANACode: loadDetails.PickupLocationIANACode,
        EquipmentTypeId: tenderDetails.truckEquipmentTypeId,
        EmptyCity: selectedLocationSuggestion.CityName,
        EmptyCityId: selectedLocationSuggestion.Value,
        EmptyGeoCode: selectedLocationSuggestion.Geolocation,
        EmptyStateCode: selectedLocationSuggestion.CityState,
        EmptyDateTime: moment
          .utc(loadDetails.PickupApptEarliest)
          .tz(loadDetails.PickupLocationIANACode)
          .subtract(1, 'days')
          .hours(10)
          .minutes(0)
          .seconds(0)
          .utc()
          .format('MM/DD/YYYY HH:mm'),
        Miles: loadDetails.Miles,
        OfferAmount: tenderDetails ? tenderDetails.carrierRate : undefined,
        CustomerRepEmailAddress: tenderDetails.carrierCommissionRepEmail || 'nsmith@arrivelogistics.com',
        OfferId: tenderDetails ? tenderDetails.offerId : undefined
      };

      setLoadingMessage('Booking Tender');
      bookItNow({ variables: { bookLoad: bookLoadVariables } });
    }
  }, [
    bookItNow,
    carrierId,
    loadDetails,
    tenderDetails,
    locationSuggestions,
    setLoadingMessage
  ]);

  if (
    (!loadingLoadDetails && errorLoadDetails?.message) ||
    (!bookLoading && bookError?.message)
  ) {
    const errorMessage = errorLoadDetails?.message || bookError?.message;
    return (
      <ErrorBooking subtitle={errorMessage} />
    );
  }

  if (loadingMessage || wasSuccessfullBook === null) {
    return (
      <CommittedCapacityLandingPageSpinner message={loadingMessage} />
    );
  }

  if (doNotExistData) {
    return (
      <h3>Not enough data to process</h3>
    );
  }

  if (wasSuccessfullBook === false) {
    return <ErrorBooking />;
  }

  return <SuccessFullyBooking />;
}
